import { Photo } from '@capacitor/camera'
import { Geolocation } from '@capacitor/geolocation'
import { spLogTags } from '@eventr/common'
import {
    ColoursObj,
    drawerStore,
    Icon,
    MobileConfirmDialog,
    TDialogStore,
    Typography,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { signal } from '@preact/signals-react'
import { useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../../i18n/i18next'
import { defaultAddSnack } from '../../../../state/overlayState/overlayContext'
import { rocketApiSignal } from '../chatProvider'
import { ActionSheetItem } from './actionSheetItem'

export const photoAttachment = signal<Photo[]>([])
export const cameraAttachment = signal<Photo | undefined>(undefined)

interface IActionSheetBodyProps {}

const SAFE_PHOTO_UPLOAD_TYPES = ['image/png', 'image/jpeg']

const SAFE_CAMERA_UPLOAD_TYPES = ['image/*']

const SAFE_FILE_UPLOAD_TYPES = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/json',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-excel',
    'application/msword',
    'application/vnd.ms-powerpoint',
]

export const UploadActionSheetBody = ({}: IActionSheetBodyProps) => {
    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const photosInput = useRef<HTMLInputElement>(null)
    const cameraInput = useRef<HTMLInputElement>(null)
    const filesInput = useRef<HTMLInputElement>(null)

    const handlePhotosClicked = () => {
        photosInput.current?.click()
    }

    const handleCameraClicked = () => {
        cameraInput.current?.click()
    }

    const handleFilesClicked = () => {
        filesInput.current?.click()
    }

    return (
        <Stack width={'100%'}>
            <input
                type="file"
                accept={SAFE_PHOTO_UPLOAD_TYPES.join(', ')}
                multiple
                ref={photosInput}
                style={{ display: 'none' }}
                onChange={(event) => {
                    for (let i = 0; i < event.target.files.length; i++) {
                        try {
                            rocketApiSignal.value
                                .sendAttachment(
                                    event.target.files[i],
                                    event.target.files[i].name
                                )
                                .then((e) => {
                                    if (e.status === 'error') {
                                        defaultAddSnack(
                                            'Could not upload file.',
                                            'error'
                                        )
                                        spLogTags(e.response, [
                                            'rocketchat',
                                            'ERROR',
                                        ])
                                    }
                                })
                            drawerStore().closeDrawer()
                        } catch (e) {
                            defaultAddSnack('Could not upload file.', 'error')
                            spLogTags(e, ['ERROR'])
                        }
                    }
                }}
            />
            <ActionSheetItem
                icon={<Icon name={'Photos'} />}
                label={
                    <Typography
                        variant={'bodyRegular'}
                        color={ColoursObj.core.primary}
                    >
                        Photos
                    </Typography>
                }
                onClick={handlePhotosClicked}
            />
            <input
                type="file"
                accept={SAFE_CAMERA_UPLOAD_TYPES.join(', ')}
                ref={cameraInput}
                style={{ display: 'none' }}
                capture
                onChange={(event) => {
                    for (let i = 0; i < event.target.files.length; i++) {
                        try {
                            rocketApiSignal.value
                                .sendAttachment(
                                    event.target.files[i],
                                    event.target.files[i].name
                                )
                                .then((e) => {
                                    if (e.status === 'error') {
                                        defaultAddSnack(
                                            'Could not upload file.',
                                            'error'
                                        )
                                        spLogTags(e.response, [
                                            'rocketchat',
                                            'ERROR',
                                        ])
                                    }
                                })
                            drawerStore().closeDrawer()
                        } catch (e) {
                            defaultAddSnack('Could not upload file.', 'error')
                            spLogTags(e, ['ERROR'])
                        }
                    }
                }}
            />
            <ActionSheetItem
                icon={<Icon name={'Camera'} />}
                label={
                    <Typography
                        variant={'bodyRegular'}
                        color={ColoursObj.core.primary}
                    >
                        Camera
                    </Typography>
                }
                onClick={handleCameraClicked}
            />
            <input
                type="file"
                accept={SAFE_FILE_UPLOAD_TYPES.join(', ')}
                ref={filesInput}
                style={{ display: 'none' }}
                multiple
                onChange={(event) => {
                    for (let i = 0; i < event.target.files.length; i++) {
                        try {
                            rocketApiSignal.value
                                .sendAttachment(
                                    event.target.files[i],
                                    event.target.files[i].name
                                )
                                .then((e) => {
                                    if (e.status === 'error') {
                                        defaultAddSnack(
                                            'Could not upload file.',
                                            'error'
                                        )
                                        spLogTags(e.response, [
                                            'rocketchat',
                                            'ERROR',
                                        ])
                                    }
                                })
                            drawerStore().closeDrawer()
                        } catch (e) {
                            defaultAddSnack('Could not upload file.', 'error')
                            spLogTags(e, ['ERROR'])
                        }
                    }
                }}
            />
            <ActionSheetItem
                icon={<Icon name={'Document'} />}
                label={
                    <Typography
                        variant={'bodyRegular'}
                        color={ColoursObj.core.primary}
                    >
                        File
                    </Typography>
                }
                onClick={handleFilesClicked}
            />
            <ActionSheetItem
                icon={<Icon name={'Location_Pin'} />}
                label={
                    <Typography
                        variant={'bodyRegular'}
                        color={ColoursObj.core.primary}
                    >
                        Location
                    </Typography>
                }
                onClick={async () => {
                    replaceDialog({
                        node: (
                            <MobileConfirmDialog
                                text="Sharing your current location means anyone on your itinerary can view this location until the itinerary ends."
                                header="Share Your Current Location"
                                onConfirm={async () => {
                                    clearDialog()
                                    const coords =
                                        await Geolocation.getCurrentPosition({
                                            enableHighAccuracy: true,
                                        }).catch((reason) => {
                                            defaultAddSnack(
                                                'Could not share location, please make sure location sharing is enabled.',
                                                'error'
                                            )
                                            spLogTags(reason, ['ERROR'])
                                        })
                                    if (coords) {
                                        drawerStore().closeDrawer()
                                        rocketApiSignal.value.sendMessage(
                                            coords.coords.latitude.toString() +
                                                ',' +
                                                coords.coords.longitude.toString(),
                                            null,
                                            { type: 'Location' }
                                        )
                                    }
                                }}
                                onCancel={() => {
                                    clearDialog()
                                }}
                                cancelText={i18n.t('general.cancel')}
                                confirmText={i18n.t('general.continue')}
                                confirmBtnTextColour={ColoursObj.core.secondary}
                            />
                        ),
                        backdropClosable: false,
                    })
                }}
            />
            <Box height="40px" />
        </Stack>
    )
}
